import React, { useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { GripVertical, Play } from 'lucide-react';
import { useT } from '@/lib/i18n';

/**
 * BeforeAfterSlider — draggable divider between the long horizontal source
 * and the short vertical clip. Purpose: make the transformation tangible.
 * Left: 16:9 YouTube-style thumbnail. Right: 9:16 clip with animated caption.
 */

const ACC = '#D4FF00';

const BeforeAfterSlider = () => {
  const { lang } = useT();
  const reduce = useReducedMotion();
  const [split, setSplit] = useState(50);
  const [playing, setPlaying] = useState(false);
  const wrapRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    setSplit(pct);
  };

  const startDrag = (e) => {
    draggingRef.current = true;
    const move = (ev) => {
      if (!draggingRef.current) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      setFromClientX(cx);
    };
    const stop = () => {
      draggingRef.current = false;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchend', stop);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchend', stop);
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(cx);
  };

  return (
    <section id="before-after" className="py-24 px-6 lg:px-8" data-testid="landing-before-after">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5 font-mono">
            {lang === 'fr' ? 'Avant · Après' : 'Before · After'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            {lang === 'fr' ? 'Un long podcast → un clip 9:16 prêt à publier' : 'One long podcast → one ready-to-post 9:16 clip'}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm sm:text-base">
            {lang === 'fr' ? 'Fais glisser la poignée pour comparer.' : 'Drag the handle to compare.'}
          </p>
        </div>

        <div
          ref={wrapRef}
          className="relative aspect-video max-w-4xl mx-auto rounded-2xl overflow-hidden border border-white/10 bg-black select-none"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onMouseEnter={() => !reduce && setPlaying(true)}
          onMouseLeave={() => setPlaying(false)}
          data-testid="before-after-frame"
        >
          {/* LEFT: original YouTube-style 16:9 */}
          <div className="absolute inset-0" data-testid="before-side">
            <YouTubeMock playing={playing} />
          </div>
          {/* RIGHT: clip 9:16 clipped in — same width container, split at N% */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 0 0 ${split}%)` }}
            data-testid="after-side"
          >
            <ClipMock playing={playing} />
          </div>
          {/* Divider handle */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-[#D4FF00] cursor-ew-resize z-10"
            style={{ left: `${split}%`, transform: 'translateX(-50%)' }}
            data-testid="before-after-divider"
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#D4FF00] text-black flex items-center justify-center shadow-2xl">
              <GripVertical className="w-4 h-4" />
            </div>
          </div>
          {/* Labels */}
          <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-widest font-mono text-zinc-300">
            Source · 16:9
          </div>
          <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-[#D4FF00]/15 border border-[#D4FF00]/40 text-[10px] uppercase tracking-widest font-mono text-[#D4FF00]">
            Clip · 9:16
          </div>
          {/* Play hint */}
          {!playing && !reduce && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-[11px] text-white/70 font-mono pointer-events-none">
              <Play className="w-3 h-3 fill-white" /> {lang === 'fr' ? 'Survole pour lire' : 'Hover to play'}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const YouTubeMock = ({ playing }) => (
  <div className="relative w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-950">
    <div className="absolute inset-0 flex items-end gap-[2px] px-6 pb-16 opacity-40">
      {Array.from({ length: 50 }).map((_, i) => (
        <motion.div
          key={i}
          className="flex-1 rounded-sm bg-white"
          animate={{ height: playing ? [`${15 + (i % 5) * 8}%`, `${45 + (i % 7) * 6}%`, `${20 + (i % 3) * 10}%`] : '30%' }}
          transition={{ duration: 1.2 + (i % 4) * 0.1, repeat: Infinity, delay: i * 0.02 }}
        />
      ))}
    </div>
    <div className="absolute bottom-4 left-4 right-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-red-500" />
        <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-white"
            animate={{ width: playing ? '100%' : '18%' }}
            transition={{ duration: 4, repeat: Infinity }}
          />
        </div>
        <span className="text-[10px] font-mono text-white/50">58:24</span>
      </div>
      <div className="text-white font-semibold text-sm">Marketing Growth · Ep 47</div>
      <div className="text-white/50 text-xs">1.2K views · 3 days ago</div>
    </div>
  </div>
);

const ClipMock = ({ playing }) => (
  <div className="relative w-full h-full bg-gradient-to-br from-purple-900/50 via-pink-900/30 to-zinc-950 flex items-center justify-center">
    {/* 9:16 vertical proxy: a portrait card centred on the width */}
    <div className="relative h-full aspect-[9/16] rounded-none overflow-hidden">
      {/* Simulated frame content */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(212,255,0,0.15),transparent_55%)]" />
      {/* Score badge */}
      <div className="absolute top-4 left-4 px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: `${ACC}25`, color: ACC, border: `1px solid ${ACC}66` }}>
        96 VIRAL
      </div>
      <div className="absolute top-4 right-4 text-[10px] font-mono text-white/70 bg-black/60 px-1.5 py-0.5 rounded">0:42</div>
      {/* Animated caption */}
      <div className="absolute bottom-24 left-4 right-4 text-center">
        <motion.div
          className="tiktok-caption text-2xl"
          animate={{ scale: playing ? [1, 1.05, 1] : 1 }}
          transition={{ duration: 0.6, repeat: Infinity }}
        >
          THIS IS THE <span className="hl">MOMENT</span><br />
          THAT <span className="hl">CHANGES</span> EVERYTHING
        </motion.div>
      </div>
      {/* Platform badges */}
      <div className="absolute bottom-6 left-4 right-4 flex justify-center gap-2">
        {[
          { label: 'TT', color: '#FF3D7F' },
          { label: 'YT', color: '#FF0000' },
          { label: 'IG', color: '#E1306C' },
        ].map((p, i) => (
          <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: p.color }}>
            {p.label}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default BeforeAfterSlider;
